/**
 * PromotedSource — boxed "source of record" block for the reading experience.
 *
 * Real-data-only: renders nothing unless a handle or a url is present. Used
 * directly under the piece body to credit where the report came from.
 *
 * Presentational only — the host page computes the props. Shared between the
 * article reader (Stage 1) and breaking-news reader (Stage 2).
 */

'use client';

import React from 'react';
import { ExternalLink } from 'lucide-react';
import { sanitizeUrl } from '@/utils/url-sanitizer';
import { formatRelativeTime } from '@/utils/date-formatting';

interface PromotedSourceProps {
  /** Twitter/X handle WITHOUT the leading @ (the @ is rendered). */
  handle?: string;
  /** Publish/observed time of the source. */
  time?: string | Date;
  /** Link to the source (tweet, article, post). */
  url?: string;
  /** Eyebrow label. Defaults to "SOURCE". */
  label?: string;
}

/** Pull a @handle out of a tweet/x.com URL when no explicit handle is given. */
function handleFromUrl(url?: string): string | undefined {
  if (!url) return undefined;
  const m = url.match(/(?:twitter\.com|x\.com)\/([A-Za-z0-9_]{1,15})(?:\/|$)/i);
  const u = m?.[1];
  // Skip non-account path roots that look like handles.
  if (!u || ['i', 'intent', 'share', 'home', 'search'].includes(u.toLowerCase())) {
    return undefined;
  }
  return u;
}

export default function PromotedSource({
  handle,
  time,
  url,
  label = 'Source',
}: PromotedSourceProps) {
  // Real-data-only: nothing to credit without a handle or a url.
  if (!handle && !url) return null;

  // Prefer an explicit handle; otherwise derive it from a tweet/x.com source URL
  // so the block never renders a dangling "From ·" with an empty slot.
  const resolvedHandle = (handle || handleFromUrl(url))?.replace(/^@/, '');
  const relTime = time ? formatRelativeTime(time) : null;

  // Build the credit line as discrete segments joined by mid-dots — so a missing
  // piece never leaves an orphaned separator.
  const segments: React.ReactNode[] = [];
  if (resolvedHandle) {
    segments.push(
      <span key="h">
        From <span className="font-semibold text-parchment">@{resolvedHandle}</span>
      </span>,
    );
  }
  if (relTime) {
    segments.push(
      <span key="t" className="text-parchment-dim">
        {relTime}
      </span>,
    );
  }
  if (url) {
    segments.push(
      <a
        key="u"
        href={sanitizeUrl(url)}
        target="_blank"
        rel="nofollow noopener"
        className="inline-flex items-center gap-1 text-parchment-dim transition-colors hover:text-scout-gold"
      >
        View source
        <ExternalLink className="h-3 w-3" />
      </a>,
    );
  }

  return (
    <div className="mt-8 rounded border-[0.5px] border-divider bg-surface px-4 py-3">
      <div className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-parchment-dim">
        {label}
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-sm text-parchment">
        {segments.map((seg, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="text-parchment-dim">·</span>}
            {seg}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
